<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CSAT_RATINGS } from 'shared/constants/messages';
import ReportSectionCard from './ReportSectionCard.vue';
import { buildRatingSegments } from './Csat/csatDistributionHelper';

const props = defineProps({
  ratingCount: {
    type: Object,
    default: () => ({}),
  },
  totalResponseCount: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t, locale } = useI18n();

const segments = computed(() =>
  buildRatingSegments(
    CSAT_RATINGS,
    props.ratingCount,
    props.totalResponseCount
  ).map(segment => ({
    ...segment,
    label: t(segment.translationKey),
    formattedPercentage: `${segment.percentage.toLocaleString(locale.value, {
      maximumFractionDigits: 1,
    })}%`,
    formattedValue: segment.value.toLocaleString(locale.value),
  }))
);

const visibleSegments = computed(() =>
  segments.value.filter(segment => segment.value > 0)
);

const chartAriaLabel = computed(() => {
  const summary = segments.value
    .map(segment => `${segment.label}: ${segment.formattedPercentage}`)
    .join(', ');
  return `${t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION')}. ${summary}`;
});
</script>

<template>
  <ReportSectionCard
    :title="$t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION')"
    :description="$t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION_DESCRIPTION')"
    icon="i-hugeicons-chart-histogram"
  >
    <div
      v-if="isLoading"
      role="status"
      :aria-label="$t('REPORT.LOADING_CHART')"
    >
      <div
        class="h-3 w-full rounded-full bg-n-alpha-2 motion-safe:animate-pulse"
      />
      <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <div
          v-for="n in 5"
          :key="n"
          class="h-12 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
        />
      </div>
    </div>

    <div v-else>
      <div
        v-if="totalResponseCount"
        data-test-id="csatDistributionBar"
        class="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-n-alpha-2"
        role="img"
        :aria-label="chartAriaLabel"
      >
        <span
          v-for="segment in visibleSegments"
          :key="segment.id"
          v-tooltip="`${segment.label}: ${segment.formattedPercentage}`"
          class="h-full first:rounded-l-full last:rounded-r-full"
          :style="{
            width: `${segment.percentage}%`,
            backgroundColor: segment.color,
          }"
        />
      </div>
      <p
        v-else
        class="mb-0 flex items-center gap-2 rounded-lg bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-11"
      >
        <span
          class="i-hugeicons-chart-histogram size-4 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        {{ $t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION_EMPTY') }}
      </p>

      <ul
        class="mb-0 mt-4 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3 lg:grid-cols-5"
      >
        <li
          v-for="segment in segments"
          :key="segment.id"
          data-test-id="csatDistributionLegendItem"
          class="flex min-w-0 flex-col gap-1 rounded-lg border border-n-weak px-3 py-2 dark:border-n-strong"
        >
          <span
            class="flex min-w-0 items-center gap-1.5 text-xs text-n-slate-11"
          >
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: segment.color }"
              aria-hidden="true"
            />
            <span aria-hidden="true">{{ segment.emoji }}</span>
            <span class="truncate">{{ segment.label }}</span>
          </span>
          <span class="flex items-baseline gap-1.5">
            <span class="text-base font-semibold tabular-nums text-n-slate-12">
              {{ segment.formattedPercentage }}
            </span>
            <span class="text-xs tabular-nums text-n-slate-10">
              {{ `(${segment.formattedValue})` }}
            </span>
          </span>
        </li>
      </ul>
    </div>
  </ReportSectionCard>
</template>
